import React, { useState } from "react";
import { Button } from "@mui/joy";
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from "@fluentui/react-components";
import { ArrowClockwiseRegular, ErrorCircleRegular } from "@fluentui/react-icons";
import "./ErrorPage.scss";

interface ErrorDetails {
    message: string;
    stack?: string;
    timestamp: string;
    url?: string;
    userAgent?: string;
    context?: string;
}

const ErrorPage: React.FC = () => {
    const [showDetails, setShowDetails] = useState(false);

    const getErrorDetails = (): ErrorDetails => {
        const stored = sessionStorage.getItem('errorDetails');
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch {
                return {
                    message: "Er is een onbekende fout opgetreden",
                    timestamp: new Date().toISOString()
                };
            }
        }
        return {
            message: "Er is een onbekende fout opgetreden",
            timestamp: new Date().toISOString()
        };
    };

    const errorDetails = getErrorDetails();

    const getErrorCause = (): string => {
        if (!errorDetails.context) {
            return "We konden de applicatie niet correct laden.";
        }

        switch (errorDetails.context) {
            case "Teams Context Check":
                return "Deze applicatie moet geopend worden in Microsoft Teams.";
            case "SSO Token Authentication":
                return "De authenticatie met Microsoft Teams is mislukt.";
            default:
                return "We konden de applicatie niet correct laden.";
        }
    };

    const handleReload = () => {
        sessionStorage.removeItem('errorDetails');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('tokenExpiry');
        window.location.hash = '#/';
        window.location.reload();
    };

    const handleCopyDetails = () => {
        const details = `
Error Details
=============
Timestamp: ${errorDetails.timestamp}
Message: ${errorDetails.message}
URL: ${errorDetails.url || window.location.href}
User Agent: ${errorDetails.userAgent || navigator.userAgent}

Stack Trace:
${errorDetails.stack || 'Niet beschikbaar'}
        `.trim();

        navigator.clipboard.writeText(details);
    };

    return (
        <div className="error-page">
            <div className="error-container">
                <div className="error-icon">
                    <ErrorCircleRegular style={{ fontSize: '80px', color: '#D13438' }} />
                </div>

                <h1 className="error-title">Er is iets misgegaan</h1>

                <p className="error-description">
                    {getErrorCause()}
                </p>

                {!errorDetails.context && (
                    <ul className="error-causes">
                        <li>Er is een probleem met de verbinding</li>
                        <li>De authenticatie is verlopen</li>
                        <li>Er is een configuratiefout</li>
                    </ul>
                )}

                <div className="error-actions">
                    <Button
                        variant="solid"
                        color="primary"
                        onClick={handleReload}
                        startDecorator={<ArrowClockwiseRegular />}
                    >
                        Opnieuw proberen
                    </Button>
                </div>

                <div className="error-additional-info">
                    <p>
                        Als het probleem aanhoudt, neem dan contact op met je systeembeheerder en
                        deel de onderstaande technische details.
                    </p>
                </div>

                <Accordion collapsible className="error-accordion">
                    <AccordionItem value="1">
                        <AccordionHeader>
                            Technische details
                        </AccordionHeader>
                        <AccordionPanel>
                            <div className="technical-details">
                                <div className="detail-row">
                                    <span className="detail-label">Tijdstip:</span>
                                    <span className="detail-value">{new Date(errorDetails.timestamp).toLocaleString('nl-NL')}</span>
                                </div>

                                <div className="detail-row">
                                    <span className="detail-label">Foutmelding:</span>
                                    <span className="detail-value error-message">{errorDetails.message}</span>
                                </div>

                                {errorDetails.url && (
                                    <div className="detail-row">
                                        <span className="detail-label">URL:</span>
                                        <span className="detail-value">{errorDetails.url}</span>
                                    </div>
                                )}

                                {errorDetails.stack && (
                                    <div className="detail-row stack-trace">
                                        <span className="detail-label">Stack Trace:</span>
                                        <pre className="detail-value">{errorDetails.stack}</pre>
                                    </div>
                                )}

                                <div className="detail-row">
                                    <span className="detail-label">Browser:</span>
                                    <span className="detail-value">{errorDetails.userAgent || navigator.userAgent}</span>
                                </div>

                                <div className="detail-actions">
                                    <Button
                                        variant="outlined"
                                        color="neutral"
                                        size="sm"
                                        onClick={handleCopyDetails}
                                    >
                                        Kopieer details
                                    </Button>
                                </div>
                            </div>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            </div>
        </div>
    );
};

export default ErrorPage;
